import { addResources, canPayResources, getResourceShortfall } from "../resources/resource-pool";
import type { ActionValidationFailure, DeployArmyAction, GameState, ResourcePool } from "../types";
import { validateAction } from "../validation/validate-action";
import { getArmyDefinitionV3ForInstance } from "./card-instances";
import { getGarrisonSnapshot, MAX_ARMY_CARDS_PER_LANE } from "./garrison";
import { getLaneSupplySnapshot } from "./supply";

export interface InvalidArmyDeploymentPreview {
  readonly legal: false;
  readonly failure: ActionValidationFailure;
}

export interface LegalArmyDeploymentPreview {
  readonly legal: true;
  readonly armyCountAfter: number;
  readonly maximumArmyCount: number;
  readonly totalArmySizeAfter: number;
  readonly commanderCapacity?: number;
  readonly laneProduction: ResourcePool;
  readonly totalUpkeepAfter: ResourcePool;
  readonly shortfallAfter: ResourcePool;
  readonly projectedSustainable: boolean;
}

export type ArmyDeploymentPreview = InvalidArmyDeploymentPreview | LegalArmyDeploymentPreview;

/**
 * Project the selected lane's next local Supply from the current board plus
 * one hypothetical legal Army. This never pays resources, advances Supply, or
 * constructs a speculative game state.
 */
export function getArmyDeploymentPreview(
  state: Readonly<GameState>,
  action: DeployArmyAction,
): ArmyDeploymentPreview {
  const validation = validateAction(state, action);
  if (!validation.valid) {
    return { legal: false, failure: validation.failure };
  }

  if (
    (state.schemaVersion !== 3 && state.schemaVersion !== 4 && state.schemaVersion !== 5) ||
    (state.catalog.schemaVersion !== 3 && state.catalog.schemaVersion !== 4)
  ) {
    throw new Error("Validated Army deployment lost its schema-3 game state.");
  }

  const definition = getArmyDefinitionV3ForInstance(state, action.armyCardInstanceId);
  const commander = state.lanes[action.laneId].players[action.playerId].commander;
  if (definition === undefined || (state.schemaVersion !== 5 && commander === null)) {
    throw new Error("Validated Army deployment lost its Army definition or required Commander.");
  }

  const garrison = getGarrisonSnapshot(state, action.playerId, action.laneId);
  const laneSupply = getLaneSupplySnapshot(state, action.laneId);
  const totalUpkeepAfter = addResources(laneSupply.upkeep, definition.upkeep);
  const shortfallAfter = getResourceShortfall(laneSupply.production, totalUpkeepAfter);

  return {
    legal: true,
    armyCountAfter: garrison.armyCount + 1,
    maximumArmyCount: MAX_ARMY_CARDS_PER_LANE,
    totalArmySizeAfter: garrison.totalArmySize + definition.armySize,
    ...(commander?.armyCapacity === undefined ? {} : { commanderCapacity: commander.armyCapacity }),
    laneProduction: { ...laneSupply.production },
    totalUpkeepAfter,
    shortfallAfter,
    projectedSustainable: canPayResources(laneSupply.production, totalUpkeepAfter),
  };
}
